import { useCallback } from 'react';

import { useContentEditorService } from '../../contexts/ContentEditorServiceContext';
import { useInlineCommentManager } from '../../contexts/InlineCommentManagerContext';
import { log } from '../../loggers/Logger';
import {
  ContentValidationError,
  SaveCommentError,
  SaveDraftError,
  SaveWorkingCopyError,
  ServiceCallError,
} from '../../services/Errors';
import type { ContentValidationIssue } from '../../services/Errors';

export type { ContentValidationIssue };

function describeSaveError(err: unknown): string {
  // Each save error already carries a composed, user-facing message — surface it directly.
  if (
    err instanceof SaveCommentError ||
    err instanceof SaveWorkingCopyError ||
    err instanceof SaveDraftError ||
    err instanceof ServiceCallError
  ) {
    return err.message;
  }
  return 'An unexpected error occurred while saving. Please try again.';
}

export type SaveContentResult =
  | { readonly ok: true }
  | {
      readonly ok: false;
      readonly errorMessage: string;
      /** Present when the save was blocked by validation, so views can render a detailed list. */
      readonly validationIssues?: readonly ContentValidationIssue[];
    };

/** Steps to save content and error handling — editors must use this instead of calling ContentEditorService directly. */
export function useSaveContent() {
  const contentEditor = useContentEditorService();
  const inlineCommentManager = useInlineCommentManager();

  const run = useCallback(
    async (persist: () => Promise<void>): Promise<SaveContentResult> => {
      const content = contentEditor.content;
      // Nothing is open to save — surface it as a failed save rather than crash.
      if (!content) return { ok: false, errorMessage: 'No content is open to save.' };

      try {
        // Comments first — closeAndSave tears down the editor, so a
        // failure after it would leave no content open to retry.
        await inlineCommentManager.saveComments();
        await persist();
        return { ok: true };
      } catch (err) {
        // Validation failures carry a detailed issue list for the UI to render.
        if (err instanceof ContentValidationError) {
          return { ok: false, errorMessage: err.message, validationIssues: err.issues };
        }
        log.error({ err }, 'Failed to save content');
        return { ok: false, errorMessage: describeSaveError(err) };
      }
    },
    [contentEditor, inlineCommentManager],
  );

  const saveAndClose = useCallback(
    () => run(() => contentEditor.closeAndSave({ isUnlock: true })),
    [run, contentEditor],
  );

  const save = useCallback(() => run(() => contentEditor.save()), [run, contentEditor]);

  return { saveAndClose, save };
}
